import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MessageBubble from './MessageBubble';
import ChatBottomBar from './ChatBottomBar';

/**
 * ChatPanel — Right panel displaying the active conversation.
 *
 * Contains header, scrollable message area, and bottom input bar.
 * Shows empty state when no conversation is selected.
 */
function ChatPanel({ conversation, currentUser, onSendMessage, onEndChat, onDeleteConversation, isActing = false }) {
  const messagesEndRef = useRef(null);
  const messageAreaRef = useRef(null);
  const menuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages?.length]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  if (!conversation) {
    return (
      <div className="msg-chat msg-chat--empty">
        <div className="msg-chat__empty-state">
          <svg className="msg-chat__empty-icon" width="80" height="80" viewBox="0 0 80 80" fill="none">
            <rect x="10" y="20" width="44" height="32" rx="8" stroke="currentColor" strokeWidth="2" opacity="0.3" />
            <rect x="26" y="30" width="44" height="32" rx="8" stroke="currentColor" strokeWidth="2" opacity="0.5" />
            <circle cx="48" cy="46" r="3" fill="currentColor" opacity="0.3" />
            <circle cx="40" cy="46" r="3" fill="currentColor" opacity="0.3" />
            <circle cx="56" cy="46" r="3" fill="currentColor" opacity="0.3" />
          </svg>
          <h2 className="msg-chat__empty-title">Select a conversation</h2>
          <p className="msg-chat__empty-text">Choose from your messages on the left</p>
        </div>
      </div>
    );
  }

  const { sender, messages, status, timeRemaining, spadesEarnable, isPaidRequest, isCreator } = conversation;

  const getHeaderStatus = () => {
    if (status === 'active') {
      return { label: 'Active chat', modifier: 'active' };
    }
    if (status === 'pending' && conversation.canSend && conversation.isCreator) {
      return { label: 'Reply to accept', modifier: 'paid' };
    }
    if (status === 'pending') {
      return { label: 'Awaiting reply', modifier: 'pending' };
    }
    if (status === 'closed') {
      return { label: 'Chat ended', modifier: 'muted' };
    }
    if (status === 'failed') {
      return { label: 'Request expired', modifier: 'muted' };
    }
    if (isPaidRequest) {
      return { label: 'Paid request', modifier: 'paid' };
    }
    if (sender.isOnline) {
      return { label: 'Online', modifier: 'online' };
    }
    return { label: 'Offline', modifier: 'muted' };
  };

  const headerStatus = getHeaderStatus();

  // Determine which messages show avatars (first in a group)
  const shouldShowAvatar = (msg, idx) => {
    if (idx === 0) return true;
    return messages[idx - 1].senderId !== msg.senderId;
  };

  const canEndChat = Boolean(isCreator && (status === 'pending' || status === 'active'));
  const canDeleteConversation = Boolean(conversation.id);

  const handleEndChatClick = async () => {
    if (!canEndChat || isActing) return;

    const confirmed = window.confirm(
      status === 'pending'
        ? 'End this chat request? The fan will be refunded if they already paid.'
        : 'End this chat?'
    );
    if (!confirmed) return;

    setIsMenuOpen(false);
    await onEndChat?.();
  };

  const handleDeleteConversationClick = async () => {
    if (!canDeleteConversation || isActing) return;

    const confirmed = window.confirm('Delete this conversation? This cannot be undone.');
    if (!confirmed) return;

    setIsMenuOpen(false);
    await onDeleteConversation?.();
  };

  return (
    <div className="msg-chat">
      {/* Header */}
      <div className="msg-chat__header">
        <div className="msg-chat__header-left">
          <img src={sender.avatar} alt="" className="msg-chat__header-avatar" />
          <div className="msg-chat__header-info">
            <span className="msg-chat__header-name">{sender.name}</span>
            <span className={`msg-chat__header-status msg-chat__header-status--${headerStatus.modifier}`}>
              {headerStatus.modifier === 'online' && <span className="msg-chat__online-dot" />}
              {(headerStatus.modifier === 'paid' || headerStatus.modifier === 'active' || headerStatus.modifier === 'pending') ? (
                <span className={`msg-chat__status-badge msg-chat__status-badge--${headerStatus.modifier}`}>
                  {headerStatus.label}
                </span>
              ) : (
                headerStatus.label
              )}
            </span>
          </div>
        </div>
        <div className="msg-chat__header-right" ref={menuRef}>
          <button
            type="button"
            className="msg-chat__header-action"
            aria-label="More options"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            disabled={isActing}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          {isMenuOpen && (
            <div className="msg-chat__menu">
              {canEndChat && (
                <button
                  type="button"
                  className="msg-chat__menu-item"
                  onClick={handleEndChatClick}
                  disabled={isActing}
                >
                  End chat
                </button>
              )}
              {canDeleteConversation && (
                <button
                  type="button"
                  className="msg-chat__menu-item msg-chat__menu-item--danger"
                  onClick={handleDeleteConversationClick}
                  disabled={isActing}
                >
                  Delete conversation
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="msg-chat__messages" ref={messageAreaRef}>
        {messages.map((msg, idx) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            senderAvatar={msg.isOwn ? currentUser.avatar : sender.avatar}
            senderName={msg.isOwn ? currentUser.name : sender.name}
            showAvatar={shouldShowAvatar(msg, idx)}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Bottom Bar */}
      <ChatBottomBar
        status={status}
        canSend={conversation.canSend}
        isCreator={conversation.isCreator}
        timeRemaining={timeRemaining}
        spadesEarnable={spadesEarnable}
        spadesBalance={currentUser.spadesBalance}
        onSend={onSendMessage}
      />
    </div>
  );
}

ChatPanel.propTypes = {
  conversation: PropTypes.object,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string.isRequired,
    spadesBalance: PropTypes.number.isRequired,
  }).isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onEndChat: PropTypes.func,
  onDeleteConversation: PropTypes.func,
  isActing: PropTypes.bool,
};

export default ChatPanel;
